"use client";

import { useEffect, useRef, useState } from "react";
import Skeleton from "react-loading-skeleton";
import styles from "./index.module.css";

const AUTO_SLIDE_INTERVAL = 20000;
const SUB_SLIDE_INTERVAL = 8000;

export default function MiniCarousel({ categories = [], setSearchTerm }) {
  const [subIndexes, setSubIndexes] = useState({});
  const [isPaused, setIsPaused] = useState(false);
  const [loadedImages, setLoadedImages] = useState({}); // ✅ NEW

  const wrapperRef = useRef(null);
  const containerRef = useRef(null);
  const touchStartX = useRef(null);
  const autoRef = useRef(null);

  const total = categories.length;

  // Duplicate for infinite wheel
  const loopedCategories = total > 0 ? [...categories, ...categories] : [];

  const getScrollStep = () => {
    if (!containerRef.current || !wrapperRef.current) return 0;

    const container = containerRef.current;
    const wrapper = wrapperRef.current;

    const firstCard = container.querySelector(`.${styles.card}`);
    if (!firstCard) return 0;

    const cardWidth = firstCard.offsetWidth;

    const gap = parseInt(window.getComputedStyle(container).gap || 0);

    const visibleWidth = wrapper.offsetWidth;

    // width of one card including gap
    const fullCardWidth = cardWidth + gap;

    // how many full cards fit in viewport
    const visibleCards = Math.floor(visibleWidth / fullCardWidth);

    // scroll exactly that many cards
    return visibleCards * fullCardWidth;
  };

  const scrollNext = () => {
    if (!containerRef.current) return;

    containerRef.current.scrollBy({
      left: getScrollStep(),
      behavior: "smooth",
    });
  };

  const scrollPrev = () => {
    if (!containerRef.current) return;

    containerRef.current.scrollBy({
      left: -getScrollStep(),
      behavior: "smooth",
    });
  };

  // ---------- Auto Scroll ----------
  useEffect(() => {
    if (isPaused || total <= 3) return;

    autoRef.current = setInterval(() => {
      scrollNext();
    }, AUTO_SLIDE_INTERVAL);

    return () => clearInterval(autoRef.current);
  }, [isPaused, total]);

  // ---------- Infinite Loop Logic ----------
  useEffect(() => {
    const container = containerRef.current;
    if (!container || total === 0) return;

    const handleScroll = () => {
      const scrollWidth = container.scrollWidth;
      const scrollLeft = container.scrollLeft;
      const half = scrollWidth / 2;

      if (scrollLeft >= half) {
        container.scrollLeft = scrollLeft - half;
      }

      if (scrollLeft < 0) {
        container.scrollLeft = half + scrollLeft;
      }
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [total]);

  // ---------- Subcategory Auto Slide ----------
  useEffect(() => {
    if (!categories.length) return;

    const interval = setInterval(() => {
      setSubIndexes((prev) => {
        const updated = { ...prev };

        categories.forEach((cat) => {
          const current = updated[cat.categoryId] || 0;
          const subLen = cat.SubCategory?.length || 0;
          if (!subLen) return;

          updated[cat.categoryId] = current + 1 >= subLen ? 0 : current + 1;
        });

        return updated;
      });
    }, SUB_SLIDE_INTERVAL);

    return () => clearInterval(interval);
  }, [categories]);

  // ---------- Swipe ----------
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (!touchStartX.current) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;

    if (diff > 50) scrollNext();
    if (diff < -50) scrollPrev();

    touchStartX.current = null;
  };

  if (!categories.length) return null;

  return (
    <div
      className={styles.carouselWrapper}
      ref={wrapperRef}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {total >= 3 && (
        <>
          <button
            className={`${styles.nav} ${styles.left}`}
            onClick={scrollPrev}
          >
            ‹
          </button>
          <button
            className={`${styles.nav} ${styles.right}`}
            onClick={scrollNext}
          >
            ›
          </button>
        </>
      )}

      <div className={styles.carouselContainer} ref={containerRef}>
        {loopedCategories.map((category, idx) => {
          const subIndex = subIndexes[category.categoryId] || 0;
          const activeSub = category.SubCategory?.[subIndex];

          return (
            <div
              key={`${category.categoryId}`}
              className={styles.card}
              onClick={() => {
                setSearchTerm(activeSub.name);
              }}
            >
              <h2>{category.name}</h2>

              {category.SubCategory?.length > 0 && (
                <div className={styles.subCarouselWrapper}>
                  <div
                    className={styles.subTrack}
                    style={{
                      transform: `translateX(-${subIndex * 100}%)`,
                    }}
                  >
                    {category.SubCategory.map((sub, subIdx) => {
                      return (
                        <div key={sub._id} className={styles.subSlide}>
                          <h4 className={styles.subTitle}>{sub.name}</h4>

                          {sub.media?.[0] && (
                            <div style={{ position: "relative" }}>
                              {!loadedImages[sub.media[0].mediaUrl] && (
                                <div
                                  style={{
                                    position: "absolute",
                                    inset: 0,
                                  }}
                                >
                                  <Skeleton width="100%" height="18rem" />
                                </div>
                              )}

                              <img
                                src={sub.media[0].mediaUrl}
                                alt={sub.name}
                                className={styles.image}
                                style={{
                                  opacity: loadedImages[sub.media[0].mediaUrl]
                                    ? 1
                                    : 0,
                                  transition: "opacity 0.3s ease",
                                }}
                                onLoad={() =>
                                  setLoadedImages((prev) => ({
                                    ...prev,
                                    [sub.media[0].mediaUrl]: true,
                                  }))
                                }
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
